// js/core/puzzleClass.js

/**
 * Main puzzle class that handles grid creation, manipulation, and validation
 * Defines the core game rules and state for a single puzzle instance.
 * Uses imported functions from validation.js and scrambling.js.
 * @class
 */
// Import necessary functions and classes
import { WordList } from '../wordlist.js'; // Use relative path
import { createEmptyGrid, swapCells as utilSwapCells } from '../utils/gridHelpers.js'; // Use relative path
import { generateDailySeed, createMulberry32Generator, shuffleArray as utilShuffleArray, randomChoice as utilRandomChoice } from '../utils/random.js'; // Use relative path
import { getValidationState, verifyLetterBalance, hasCorrectPositions, validateGridStructure } from './validation.js'; // Use relative path
import { fixLetterBalance } from './scrambling.js'; // Use relative path

export class SqudlePuzzle {

  constructor(gridSize = 4, seed = null) {
    // Validation for WordList happens implicitly via import

    this.gridSize = gridSize;
    // Use imported utility functions
    this.grid = createEmptyGrid(this.gridSize);
    this.solvedGrid = createEmptyGrid(this.gridSize);
    this.swapCount = 0;
    this.hintUsed = false; // Added hintUsed state
    // REMOVE: this.randomStateObj = { seed: 0 };

    // Select the correct word list based on size from the main WordList object
    this.validWordSet = new Set(WordList[gridSize] || []); // Use WordList[gridSize]
    if (this.validWordSet.size === 0) {
        console.error(`CRITICAL: No words found in WordList for size ${gridSize}!`);
    }

    // Use imported utility function
    this.seed = seed !== null ? seed : generateDailySeed();
    // ADD: Create the generator instance
    this.randomGenerator = createMulberry32Generator(this.seed);
    console.log(`SqudlePuzzle initialized: Size=${gridSize}, Seed=${this.seed}, WordSet Size=${this.validWordSet.size}`);
  }

  /** Seeded random number generator for this puzzle instance */
  random() {
    // Use the generator function directly
    return this.randomGenerator();
  }

  /** Seeded random choice from an array */
  randomChoice(array) {
    // Use imported utility function
    return utilRandomChoice(array, this.random.bind(this));
  }

  /** Seeded array shuffle */
  shuffleArray(array) {
    // Use imported utility function
    return utilShuffleArray(array, this.random.bind(this));
  }

  initializeGrid() {
    // Use imported utility function
    this.grid = createEmptyGrid(this.gridSize);
    this.solvedGrid = createEmptyGrid(this.gridSize);
    this.swapCount = 0;
    this.hintUsed = false;
    // Reset generator using the original seed
    this.randomGenerator = createMulberry32Generator(this.seed);
  }

  /** Validates the internally stored solvedGrid using imported function */
  validateSolution() {
     console.log("Validating internal solvedGrid...");
     // Use imported function
     return validateGridStructure(this.solvedGrid, this.validWordSet);
  }

  /** Checks if the current grid matches the solved grid */
  isSolved() {
    if (!this.grid || !this.solvedGrid || this.grid.length !== this.gridSize || this.solvedGrid.length !== this.gridSize) { return false; }
    for (let row = 0; row < this.gridSize; row++) {
       if (!this.grid[row] || !this.solvedGrid[row] || this.grid[row].length !== this.gridSize || this.solvedGrid[row].length !== this.gridSize) { return false; }
      for (let col = 0; col < this.gridSize; col++) { if (this.grid[row][col] !== this.solvedGrid[row][col]) { return false; } }
    }
    return true;
  }

  /** Swaps two cells in the current grid if they are valid and not locked */
  swapCells(row1, col1, row2, col2) {
    // Basic bounds checks
    if (row1 < 0 || row1 >= this.gridSize || col1 < 0 || col1 >= this.gridSize ||
        row2 < 0 || row2 >= this.gridSize || col2 < 0 || col2 >= this.gridSize) {
      console.warn(`Swap failed: Out of bounds coordinates.`);
      return false;
    }
    // Don't swap the same cell
    if (row1 === row2 && col1 === col2) { return false; }

    // Check if tiles are locked (green)
    const validationState = this.getValidationState();
    if (!validationState) {
        console.error("Swap failed: Could not get validation state.");
        return false;
    }
    // Check grid dimensions match validation state
    if (validationState.length !== this.gridSize || !validationState[row1] || validationState[row1].length !== this.gridSize) {
        console.error("Swap failed: Mismatch between grid size and validation state.");
        return false;
    }
    if (validationState[row1][col1] === 4 || validationState[row2][col2] === 4) {
        console.log("Swap failed: One or both tiles are locked (green).");
        return false;
    }


    // Perform the swap using imported utility function
    const success = utilSwapCells(this.grid, row1, col1, row2, col2);

    if (success) { this.swapCount++; }
    return success;
  }

  /** Gets the validation state grid using imported function */
  getValidationState() {
    // Use imported function
    return getValidationState(this.grid, this.solvedGrid);
  }

  /** Finds a hint (a swap that moves one letter to its correct final position) */
  getHint() {
    if (this.isSolved()) { return null; } // No hints if already solved

    // Ensure letter balance first (important for hint logic)
    // Use imported functions
    if (!verifyLetterBalance(this.grid, this.solvedGrid)) {
        console.warn("Hint generation: Fixing letter imbalance before finding hint.");
        fixLetterBalance(this.grid, this.solvedGrid); // Use imported function
        if (!verifyLetterBalance(this.grid, this.solvedGrid)) { // Re-verify
            console.error("Hint generation failed: Could not fix letter balance.");
            return null;
        }
    }

    const validationState = this.getValidationState(); // Uses imported function
    // Add check for validationState validity
    if (!validationState || validationState.length !== this.gridSize) {
        console.error("Hint generation failed: Invalid validation state returned.");
        return null;
    }

    // Collect locked positions
    const lockedTiles = new Set();
    for (let r = 0; r < this.gridSize; r++) {
      // Add check for row validity
      if (!validationState[r] || validationState[r].length !== this.gridSize) continue;
      for (let c = 0; c < this.gridSize; c++) {
        if (validationState[r][c] === 4) {
          lockedTiles.add(`${r},${c}`);
        }
      }
    }

    // Find possible hints: swaps that place a letter correctly
    const possibleHints = [];
    for (let targetR = 0; targetR < this.gridSize; targetR++) {
      for (let targetC = 0; targetC < this.gridSize; targetC++) {
        const targetKey = `${targetR},${targetC}`;
        // Skip if target is already correct or locked
         if (!this.grid[targetR] || !this.solvedGrid[targetR] || // Check row validity
            this.grid[targetR][targetC] === this.solvedGrid[targetR][targetC] ||
            lockedTiles.has(targetKey)) {
            continue;
        }

        const correctLetterForTarget = this.solvedGrid[targetR][targetC];

        // Find where this correct letter currently is
        let foundSource = false;
        for (let sourceR = 0; sourceR < this.gridSize; sourceR++) {
          // Add check for row validity
          if (!this.grid[sourceR]) continue;
          for (let sourceC = 0; sourceC < this.gridSize; sourceC++) {
            const sourceKey = `${sourceR},${sourceC}`;
            // Skip if source is the target, or if source is locked
            if ((sourceR === targetR && sourceC === targetC) || lockedTiles.has(sourceKey)) {
                continue;
            }

            // If we found the correct letter at an incorrect, unlocked position
            if (this.grid[sourceR][sourceC] === correctLetterForTarget) {
              possibleHints.push({ sourceRow: sourceR, sourceCol: sourceC, targetRow: targetR, targetCol: targetC });
              foundSource = true;
              break; // Found the source for this target, move to next target
            }
          }
          if (foundSource) break; // Move to next target row
        }
      }
    }

    if (possibleHints.length > 0) {
      // Use the seeded random choice to pick one hint deterministically
      return this.randomChoice(possibleHints);
    }

    console.log("No suitable hint found.");
    return null; // No hint found
  }

  /** Logs the solved grid to the console for debugging */
  logPuzzleSolution() {
      if (!this.solvedGrid || !Array.isArray(this.solvedGrid) || this.solvedGrid.length === 0) {
          console.error("Cannot log solution: solvedGrid is invalid or empty.");
          return;
      }
      console.log("%c Squdle - Solution Grid", "font-size: 14px; font-weight: bold; color: #3498db;");
      let solutionStr = "\n";
      const separator = "+".padEnd(this.gridSize * 4 + 1, "-") + "+\n"; // Adjust padding
      solutionStr += separator;
      for (let row = 0; row < this.gridSize; row++) {
          if (!this.solvedGrid[row] || !Array.isArray(this.solvedGrid[row]) || this.solvedGrid[row].length !== this.gridSize) {
              solutionStr += `| ERROR - Invalid Row ${row}`.padEnd(this.gridSize * 4) + "|\n" + separator;
              continue;
          }
          solutionStr += "|";
          for (let col = 0; col < this.gridSize; col++) {
              const cellValue = this.solvedGrid[row][col];
              solutionStr += " " + (cellValue !== undefined && cellValue !== '' ? cellValue : '?') + " |"; // Adjust padding, show '?' for empty
          }
          solutionStr += "\n" + separator;
      }
      console.log(solutionStr);
       if (this.validateSolution()) {
           console.log("%c ✓ Logged solution is valid.", "color: green; font-weight: bold;");
       } else {
           console.warn("%c ✗ Logged solution appears INVALID based on current validation rules.", "color: red; font-weight: bold;");
       }
  }
}
// Removed window export
